// =============================================================================
// FILE: MESStatusBar.js
// PPE → MES → Live Status Bar
//
// PURPOSE:
//   Displays live shopfloor execution summary.
//
// UI UPGRADE (2026-02-26):
//   ✔ Gradient KPI pills
//   ✔ Live system pulse indicator
//   ✔ Shift timer
//   ✔ Industrial MES styling
// =============================================================================

import React, { useEffect, useState } from "react";
import {
  fetchReadyOperations,
  fetchRunningExecutions,
} from "../services/mesExecutionService";

// -----------------------------------------------------------------------------
// KPI STYLE HELPERS
// -----------------------------------------------------------------------------
const pillStyle = (gradient) => ({
  background: gradient,
  color: "#fff",
  padding: "10px 18px",
  borderRadius: "999px",
  fontWeight: 600,
  minWidth: "150px",
  textAlign: "center",
  fontSize: "14px",
  boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
});

// -----------------------------------------------------------------------------
// COMPONENT
// -----------------------------------------------------------------------------
const MESStatusBar = () => {
  const [readyCount, setReadyCount] = useState(0);
  const [runningCount, setRunningCount] = useState(0);
  const [shiftTime, setShiftTime] = useState("00:00:00");

  // ---------------------------------------------------------------------------
  // Load MES counts
  // ---------------------------------------------------------------------------
  const loadStatus = async () => {
    try {
      const [ready, running] = await Promise.all([
        fetchReadyOperations(),
        fetchRunningExecutions(),
      ]);

      setReadyCount(ready?.length || 0);
      setRunningCount(running?.length || 0);
    } catch (err) {
      console.error("Failed to load MES status", err);
    }
  };

  // ---------------------------------------------------------------------------
  // Shift Timer (live clock)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const h = String(now.getHours()).padStart(2, "0");
      const m = String(now.getMinutes()).padStart(2, "0");
      const s = String(now.getSeconds()).padStart(2, "0");
      setShiftTime(`${h}:${m}:${s}`);
    };

    updateClock();
    const timer = setInterval(updateClock, 1000);

    return () => clearInterval(timer);
  }, []);

  // ---------------------------------------------------------------------------
  // Poll MES status every 8 seconds
  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 8000);
    return () => clearInterval(interval);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        display: "flex",
        gap: "14px",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {/* READY */}
      <div
        style={pillStyle(
          "linear-gradient(135deg,#2563eb,#3b82f6)"
        )}
      >
        🔵 Ready: {readyCount}
      </div>

      {/* RUNNING 
      "linear-gradient(135deg,#16a34a,#22c55e)"*/}
      <div
        style={pillStyle(
          
          "linear-gradient(135deg,#0ea5e9,#38bdf8)"
        )}
      >
        🟢 Running: {runningCount}
      </div>

      {/* SHIFT TIMER */}
      <div
        style={{
          background: "rgba(255,255,255,0.15)",
          padding: "10px 18px",
          borderRadius: "999px",
          fontWeight: 600,
          backdropFilter: "blur(6px)",
        }}
      >
        ⏱ Shift Time: {shiftTime}
      </div>

      {/* LIVE INDICATOR */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "13px",
        }}
      >
        <span
          style={{
            width: "10px",
            height: "10px",
            background: "#22c55e",
            borderRadius: "50%",
            animation: "pulse 1.5s infinite",
          }}
        />
        LIVE
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.9); opacity: 0.7; }
          50% { transform: scale(1.3); opacity: 1; }
          100% { transform: scale(0.9); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default MESStatusBar;